import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/Buttons/Button';
import Note from 'components/Note';
import StatCard from 'components/Statistics/StatCard';
import IOFileLoading from 'components/IOFileLoading/components';
import ShareholdersSelect from 'components/ShareholdersSelect';
import Table from 'components/Table';
import {
  Avatar, Breadcrumb, Card, InputSlider, Tag,
} from 'xland-ui';
import { CloudDownload, LineChart, ScrollText } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { formatDate, formatNumber } from 'utils/functions';
import { downloadReportShareholder, getShareholderPosition } from '../modules/actions';
import MyPositionOverview from './PositionOverview';
import MyPositionResources from './PositionResources';
import MyPositionValuation from './PositionValuation';
import './styles.scss';

function MyPosition(props) {
  const {
    literals,
    literalsCommon,
    literalsCaptable,
    startup,
    currency,
  } = props;

  const [shareholders, setShareholders] = useState([]);
  const [position, setPosition] = useState();
  const [resource, setResource] = useState('shares');
  const [loading, setLoading] = useState(true);
  const [lastValuation, setLastValuation] = useState({ real: 0, new: 0, pps: 0 });
  const [downloadingReport, setDownloadingReport] = useState(false);

  const [searchParams] = useSearchParams();
  const taxIds = searchParams?.get('taxId')?.split(',') || [];

  useEffect(() => {
    if (shareholders && startup.id) {
      (async () => {
        setLoading(true);
        const auxPosition = await getShareholderPosition(startup.id, { shareholders: shareholders.join(',') });
        setPosition(auxPosition);
        const { valuation, pps, date } = auxPosition.valuation[auxPosition.valuation.length - 1];
        setLastValuation({
          real: valuation, new: valuation, pps, date,
        });
        setLoading(false);
      })();
    }
  }, [shareholders, startup.id]);

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    const date = formatDate(null, { format: 'dmY' });
    const shareholderName = shareholders.length > 1 ? 'reports' : 'shareholder';
    const filename = [date, shareholderName, startup.name].join('_');
    const ext = shareholders.length > 1 ? 'zip' : 'pdf';
    await downloadReportShareholder(startup.id, shareholders, `${filename}.${ext}`.replace(/\s/g, ''));
    setDownloadingReport(false);
  };

  const columns = [
    {
      width: 35, title: () => startup?.name, preRender: () => <Avatar title={startup.name} src={startup?.logo?.path} />,
    },
    {
      label: literalsCommon.name, preRender: () => startup?.name, classes: 'fc-highest border-right',
    },
    {
      field: 'shares',
      label: literals.entryDate,
      preRender: v => (v?.entryDate ? formatDate(v.entryDate) : '-'),
      width: 120,
    },
    {
      field: 'shares',
      label: literalsCaptable.ownership,
      preRender: v => formatNumber(v?.ownership, '-', { symbol: '%', dec: 2 }),
      width: 120,
    },
    {
      field: 'shares',
      label: literalsCaptable.fullyDilutedAcronym,
      title: literalsCaptable.fullyDiluted,
      preRender: v => formatNumber(v?.fullyDiluted, '-', { symbol: '%', dec: 2 }),
      width: 120,
    },
    {
      field: 'shares',
      label: 'Votes',
      preRender: v => formatNumber(v.votes, '-', { symbol: '%', dec: 2 }),
      width: 120,
    },
    {
      field: 'shares',
      label: 'Shares',
      preRender: v => (<span title={`${v.votes}% ${literalsCaptable.vote}`}>{formatNumber(v.number, '-')}</span>),
      width: 120,
    },
    {
      field: 'shares',
      label: 'C.Shares',
      preRender: v => v.capital,
      width: 120,
    },
    {
      field: 'debts',
      label: 'C.Debts',
      preRender: v => v.pending,
      width: 120,
    },
    {
      field: 'options',
      label: literalsCaptable.options,
      title: `${literalsCaptable.stockOption} | ${literalsCaptable.phantom}`,
      preRender: v => `${formatNumber(v.number, '-')}`,
      width: 110,
    },
  ];

  function formatQuarterYear(date) {
    if (!date) return '(QX \'XX)';

    const d = new Date(date);
    const quarter = Math.floor(d.getMonth() / 3) + 1;
    const year = d.getFullYear().toString().slice(-2);
    return `(Q${quarter} '${year})`;
  }

  const tagValue = useMemo(() => (!lastValuation.real ? 0 : (
    ((lastValuation.new - lastValuation.real) / lastValuation.real) * 100
  )), [lastValuation.new, lastValuation.real]);

  return (
    <div className='myPosition-wrapper'>
      <div className='myPosition-header'>
        <Breadcrumb routes={[{ name: literals.title }]} />

        <div className='buttons mb-sp'>
          <Button
            color='tertiary'
            icon={CloudDownload}
            text={literalsCaptable.certificate}
            onClick={handleDownloadReport}
          />
          <ShareholdersSelect
            mode='position'
            taxIds={taxIds}
            startup={startup.id}
            positionShareholders={shareholders}
            setShareholders={setShareholders}
          />
        </div>
      </div>
      <div className='myPosition-valuation-wrapper'>
        <Card className='valuation-stat'>
          <Tag
            className='mb-lb'
            type={tagValue < 0 ? 'danger' : 'success'}
            text={tagValue < 100 ? `${tagValue.toFixed(2)}%` : `${(tagValue / 100 + 1).toFixed(2)}x`}
          />
          <StatCard
            className='border-0 p-0 bg-transparent'
            title={`${literals.latestValuation} ${formatQuarterYear(lastValuation.date)}`}
            value={formatNumber(lastValuation.real, '-', { symbol: currency.symbol })}
            icon={ScrollText}
            loading={loading}
          />
        </Card>

        <Card className='myPosition-slider'>
          <h4 className='myPosition-slider--title'>
            <LineChart className='me-2' />
            {literals.latestCompanyValuation}
          </h4>
          <InputSlider
            minValue={lastValuation.real / 10}
            maxValue={lastValuation.real * 10}
            value={lastValuation.new}
            onChange={v => setLastValuation(prev => ({ ...prev, new: v }))}
            symbol={currency.symbol}
            loading={loading}
            editable
          />
        </Card>
      </div>
      <Table
        className='mb-sp'
        key={loading}
        searcher={false}
        columns={columns}
        fetch={!loading ? () => ({ items: [position], total: 0 }) : null}
        pageSize={1}
      />
      <div className='row'>
        <div className='col-12 col-xl-7'>
          <MyPositionOverview
            literals={literals}
            position={position}
            lastPPS={(lastValuation.new / lastValuation.real) * lastValuation.pps}
            currency={currency}
            resource={resource}
            setResource={setResource}
            loading={loading}
          />
        </div>
        <div className='col-12 col-xl-5'>
          <MyPositionValuation
            literals={literals}
            valuations={position?.valuation || []}
            lastValuation={lastValuation.new}
            lastPPS={(lastValuation.new / lastValuation.real) * lastValuation.pps}
            currency={currency}
            loading={loading}
          />
        </div>
      </div>
      <MyPositionResources
        literals={literals}
        literalsCommon={literalsCommon}
        position={position}
        resource={['vestedOptions', 'pendingOptions'].includes(resource) ? 'options' : resource}
        shareholders={shareholders.join(',')}
        startup={startup.id}
        currency={currency}
        loading={loading}
      />

      <Note type='startup' id={startup.id} />
      {downloadingReport && (
        <IOFileLoading
          mode='download'
          file={{ name: `${shareholders}_report.pdf` }}
        />
      )}
    </div>
  );
}

MyPosition.propTypes = {
  literals: PropTypes.object.isRequired,
  literalsCommon: PropTypes.object.isRequired,
  literalsCaptable: PropTypes.object.isRequired,
  startup: PropTypes.object.isRequired,
  currency: PropTypes.object.isRequired,
};

export default MyPosition;
